/** The webhook & provider-event logs (docs/webhook-logs.md): Russian labels and badge tones for
 *  the vocabulary of `payment_webhook_log` / `generation_event_log`, the list query of the
 *  «Вебхуки» page and the grouping of its stats chips. Pure, no I/O.
 *
 *  The outcome / source lists mirror PAYMENT_LOG_* (src/core/models/payment.py) and GEN_LOG_*
 *  (src/core/models/job.py); an unknown value is shown as is with a neutral tone. */

import { CHANNEL_LABELS } from '@/lib/paymentLabels'
import { paymentStatus } from '@/lib/status'

export type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'live'
export interface Badge {
  tone: Tone
  label: string
}

export type LogKind = 'payments' | 'generation'

/** GET /webhooks/payments items (src/api/admin/webhooks.py `_payment_item`). */
export interface PaymentLogItem {
  id: number
  received_at: string | null
  source: string
  provider: string
  method: string | null
  gateway: string | null
  payment_id: string | null
  user_id: number | null
  event_status: string | null
  outcome: string
  signature_ok: boolean | null
  http_status: number | null
  duration_ms: number | null
  error: string | null
  /** the server can run it through the handler again (verified webhook, stored body);
   *  absent on older servers */
  reprocessable?: boolean
}

/** GET /webhooks/generation items (`_generation_item`). */
export interface GenerationLogItem {
  id: number
  received_at: string | null
  provider: string
  source: string
  job_id: string | null
  user_id: number | null
  provider_job_id: string | null
  signature_ok: boolean | null
  outcome: string
  provider_status: string | null
  error: string | null
  duration_ms: number | null
  reprocessable?: boolean
}

/** The detail endpoints add the redacted body and the allow-listed headers (null once erased). */
export interface LogDetailExtra {
  payload: unknown
  headers: Record<string, unknown> | null
}
export type PaymentLogDetail = PaymentLogItem & LogDetailExtra
export type GenerationLogDetail = GenerationLogItem & LogDetailExtra

export interface LogPage<T> {
  total: number
  items: T[]
}

export interface LogStats {
  hours: number
  payments: StatRow[] | null
  generation: StatRow[] | null
}

/** Bodies that nobody vouched for: shown as text, with a warning (docs/webhook-logs.md). */
export function isUnverified(outcome: string): boolean {
  return outcome === 'bad_signature' || outcome === 'no_key' || outcome === 'too_large'
}

export const PAYMENT_OUTCOMES = [
  'applied',
  'ignored',
  'unknown_order',
  'bad_signature',
  'foreign_terminal',
  'no_key',
  'too_large',
  'bad_body',
  'error',
  'received',
] as const

export const GENERATION_OUTCOMES = [
  'accepted',
  'status_change',
  'completed',
  'failed',
  'duplicate',
  'unknown_job',
  'bad_signature',
  'bad_body',
  'error',
] as const

export const PAYMENT_SOURCES = ['webhook', 'poll', 'telegram'] as const
export const GENERATION_SOURCES = ['webhook', 'poll', 'submit'] as const

/** `payment_webhook_log.provider` values (sbp:unknown = a gateway name we do not know). */
export const PAYMENT_PROVIDERS = [
  'heleket',
  'sbp:paygatecore',
  'sbp:idmpay',
  'sbp:wata',
  'sbp:unknown',
  'stars',
] as const
export const GENERATION_PROVIDERS = ['fal', 'wavespeed', 'higgsfield', 'mcp', 'gemini', 'text'] as const

const PAYMENT_OUTCOME: Record<string, Badge> = {
  applied: { tone: 'ok', label: 'Применено' },
  ignored: { tone: 'neutral', label: 'Без изменений' },
  unknown_order: { tone: 'warn', label: 'Заказ не найден' },
  bad_signature: { tone: 'danger', label: 'Подпись не сошлась' },
  foreign_terminal: { tone: 'danger', label: 'Чужой терминал' },
  no_key: { tone: 'warn', label: 'Нет ключа' },
  too_large: { tone: 'danger', label: 'Слишком большое тело' },
  bad_body: { tone: 'danger', label: 'Тело не разобрать' },
  error: { tone: 'danger', label: 'Ошибка обработки' },
  received: { tone: 'neutral', label: 'Принято без итога' },
}

const GENERATION_OUTCOME: Record<string, Badge> = {
  accepted: { tone: 'ok', label: 'Принят' },
  status_change: { tone: 'neutral', label: 'Смена статуса' },
  completed: { tone: 'ok', label: 'Готово' },
  failed: { tone: 'danger', label: 'Провайдер: ошибка' },
  duplicate: { tone: 'neutral', label: 'Повтор' },
  unknown_job: { tone: 'warn', label: 'Задача не найдена' },
  bad_signature: { tone: 'danger', label: 'Подпись не сошлась' },
  bad_body: { tone: 'danger', label: 'Тело не разобрать' },
  error: { tone: 'danger', label: 'Ошибка отправки' },
}

const SOURCE: Record<string, string> = {
  webhook: 'Вебхук',
  poll: 'Опрос',
  telegram: 'Telegram',
  submit: 'Отправка задачи',
}

const PAYMENT_PROVIDER: Record<string, string> = {
  heleket: CHANNEL_LABELS['crypto:heleket'] ?? 'Heleket',
  'sbp:paygatecore': CHANNEL_LABELS['sbp:paygatecore'] ?? 'PayGateCore',
  'sbp:idmpay': CHANNEL_LABELS['sbp:idmpay'] ?? 'IDMPay',
  'sbp:wata': CHANNEL_LABELS['sbp:wata'] ?? 'WATA',
  'sbp:unknown': 'СБП, неизвестный шлюз',
  stars: CHANNEL_LABELS.stars ?? 'Telegram Stars',
}

const GENERATION_PROVIDER: Record<string, string> = {
  fal: 'fal.ai',
  wavespeed: 'WaveSpeed',
  higgsfield: 'Higgsfield',
  mcp: 'Higgsfield MCP',
  gemini: 'Gemini',
  text: 'Текстовые модели',
}

export function outcomeBadge(kind: LogKind, outcome: string): Badge {
  const map = kind === 'payments' ? PAYMENT_OUTCOME : GENERATION_OUTCOME
  return map[outcome] ?? { tone: 'neutral', label: outcome }
}

export function sourceLabel(source: string): string {
  return SOURCE[source] ?? source
}

export function providerLabel(kind: LogKind, provider: string): string {
  const map = kind === 'payments' ? PAYMENT_PROVIDER : GENERATION_PROVIDER
  return map[provider] ?? provider
}

/** Signature check of the event: verified, failed, or not checked (polls, Telegram, a body
 *  too large to read, no key). Colour + word, never colour alone (rule 21). */
export function signatureBadge(ok: boolean | null | undefined): Badge {
  if (ok === true) return { tone: 'ok', label: 'Подпись верна' }
  if (ok === false) return { tone: 'danger', label: 'Подпись не сошлась' }
  return { tone: 'neutral', label: 'Не проверялась' }
}

export function outcomesOf(kind: LogKind): readonly string[] {
  return kind === 'payments' ? PAYMENT_OUTCOMES : GENERATION_OUTCOMES
}
export function sourcesOf(kind: LogKind): readonly string[] {
  return kind === 'payments' ? PAYMENT_SOURCES : GENERATION_SOURCES
}
export function providersOf(kind: LogKind): readonly string[] {
  return kind === 'payments' ? PAYMENT_PROVIDERS : GENERATION_PROVIDERS
}

// ── list query ──

export interface LogFilters {
  provider: string
  outcome: string
  source: string
  /** payments: our payment id; generation: our job id (both UUIDs) */
  ref: string
  /** users.id (not the Telegram id) */
  user: string
  from: string // yyyy-mm-dd, '' = unset
  to: string
}

export const EMPTY_FILTERS: LogFilters = {
  provider: '',
  outcome: '',
  source: '',
  ref: '',
  user: '',
  from: '',
  to: '',
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const USER_ID = /^\d{1,18}$/

export function isUuid(s: string): boolean {
  return UUID.test(s.trim())
}

export type LogQuery =
  | { ok: true; path: string }
  | { ok: false; errors: Partial<Record<'ref' | 'user', string>> }

/** The list request of one tab, or the field errors that stop it (the API would answer 400 for
 *  a malformed UUID — say it under the field instead, with what to type). */
export function logListQuery(
  kind: LogKind,
  f: LogFilters,
  offset: number,
  limit: number,
): LogQuery {
  const errors: Partial<Record<'ref' | 'user', string>> = {}
  const ref = f.ref.trim()
  const user = f.user.replace(/\s/g, '')
  if (ref && !isUuid(ref)) {
    errors.ref =
      kind === 'payments'
        ? 'Нужен полный id платежа (UUID), например из карточки платежа'
        : 'Нужен полный id заявки (UUID), например из карточки заявки'
  }
  if (user && !USER_ID.test(user)) errors.user = 'Нужен числовой id пользователя'
  if (errors.ref || errors.user) return { ok: false, errors }

  const qs = new URLSearchParams()
  qs.set('limit', String(limit))
  qs.set('offset', String(offset))
  if (f.provider) qs.set('provider', f.provider)
  if (f.outcome) qs.set('outcome', f.outcome)
  if (f.source) qs.set('source', f.source)
  if (ref) qs.set(kind === 'payments' ? 'payment_id' : 'job_id', ref.toLowerCase())
  if (user) qs.set('user_id', user)
  if (f.from) qs.set('date_from', f.from)
  if (f.to) qs.set('date_to', f.to)
  return { ok: true, path: `/webhooks/${kind}?${qs.toString()}` }
}

// ── stats chips ──

export interface StatRow {
  provider: string
  outcome: string
  count: number
}

export interface StatGroup {
  provider: string
  total: number
  /** outcomes of this provider, the most frequent first */
  outcomes: { outcome: string; count: number }[]
  /** events that were not applied cleanly (bad signature, errors, unknown order…) */
  problems: number
}

const CLEAN = new Set(['applied', 'ignored', 'received', 'accepted', 'status_change', 'completed', 'duplicate'])

/** GET /webhooks/stats rows → one group per provider (the busiest first), with a count of
 *  events that need a look (anything but a clean outcome). */
export function groupStats(rows: StatRow[] | null | undefined): StatGroup[] {
  const by = new Map<string, StatGroup>()
  for (const r of rows ?? []) {
    const g = by.get(r.provider) ?? { provider: r.provider, total: 0, outcomes: [], problems: 0 }
    g.total += r.count
    g.outcomes.push({ outcome: r.outcome, count: r.count })
    if (!CLEAN.has(r.outcome)) g.problems += r.count
    by.set(r.provider, g)
  }
  const groups = [...by.values()]
  for (const g of groups) g.outcomes.sort((a, b) => b.count - a.count || a.outcome.localeCompare(b.outcome))
  return groups.sort((a, b) => b.total - a.total || a.provider.localeCompare(b.provider))
}

function plural(n: number, one: string, few: string, many: string): string {
  const d10 = n % 10
  const d100 = n % 100
  if (d10 === 1 && d100 !== 11) return one
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few
  return many
}

/** The stats window in words: 24 → '24 часа', 168 → '7 дней', 1 → '1 час'. */
export function periodText(hours: number): string {
  if (hours > 24 && hours % 24 === 0) {
    const d = hours / 24
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`
  }
  return `${hours} ${plural(hours, 'час', 'часа', 'часов')}`
}

/** Human duration of a handler: '85 мс', '1,2 с'. */
export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms} мс`
  return `${(ms / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} с`
}

// ── «Обработать повторно» (POST /webhooks/{kind}/{id}/reprocess, step-up 2FA) ──

export interface PaymentReprocessResult {
  event_id: number
  outcome: 'applied' | 'ignored' | 'unknown_order'
  changed: boolean
  payment_id: string | null
  status_before: string | null
  status_after: string | null
}

export interface GenerationReprocessResult {
  event_id: number
  outcome: 'requeued' | 'duplicate'
  changed: boolean
  job_id: string | null
  job_status: string | null
}

export type ReprocessResult = PaymentReprocessResult | GenerationReprocessResult

/** The permissions a reprocess needs (src/api/admin/webhooks.py): money for a payment event,
 *  a job retry for a generation one — besides reading the journal. */
export function reprocessPermissions(kind: LogKind): [string, string] {
  return kind === 'payments' ? ['webhooks.view', 'payments.refund'] : ['webhooks.generation', 'jobs.retry']
}

/** The confirm dialog's body. */
export function reprocessConfirmBody(kind: LogKind): string {
  return kind === 'payments'
    ? 'Событие пройдёт тот же обработчик, что и при получении, с сохранённым телом. Уже зачтённый платёж не зачтётся второй раз: повтор безопасен. Нужен код 2FA.'
    : 'Заявка будет снова передана на завершение. Готовую или закрытую заявку повтор не изменит. Нужен код 2FA.'
}

/** What the result line says: tone + word (rule 21). */
export function reprocessResultBadge(r: ReprocessResult): Badge {
  if ('job_id' in r) {
    return r.changed
      ? { tone: 'ok', label: 'Заявка снова передана на завершение' }
      : { tone: 'neutral', label: 'Заявка уже закрыта: ничего не изменилось' }
  }
  if (r.outcome === 'unknown_order') return { tone: 'warn', label: 'Платёж не найден: ничего не изменилось' }
  if (r.changed) {
    return {
      tone: 'ok',
      label:
        r.status_before && r.status_after
          ? `Применено: статус платежа был «${paymentStatus(r.status_before).label}», стал «${paymentStatus(r.status_after).label}»`
          : 'Применено',
    }
  }
  return { tone: 'neutral', label: 'Уже применено раньше: ничего не изменилось' }
}
